import {
  formatTime,
  formatCurrency,
  getOrderTypeLabel,
  getPaymentMethodLabel,
  extractItemData,
  buildGroupedItemsHTML,
  calculateOrderTotals
} from '@/utils/printHelpers';

export interface CustomerPrintOrder {
  order_number: string | number;
  ticket_number?: string;
  created_at: string;
  table_number?: string | null;
  customer_name?: string | null;
  waiter?: { name: string };
  waiter_name?: string | null;
  order_type: string;
  items?: any[];
  persons?: any[];
  payment_method?: string;
  amount_paid?: number;
  iva_enabled?: boolean;
  iva_rate?: number;
}

export const createHeaderSection = (restaurant?: { name?: string; address?: string; phone?: string }) => `
  <div class="receipt-header">
    <div class="restaurant-name">${restaurant?.name || 'Restaurante'}</div>
    <div class="restaurant-info">
      ${restaurant?.address ? `${restaurant.address}<br>` : ''}
      ${restaurant?.phone ? `Tel: ${restaurant.phone}<br>` : ''}
    </div>
  </div>
`;

export const createOrderInfoSection = (order: CustomerPrintOrder, isPreBill = false) => {
  const infoRows: string[] = [];

  if (isPreBill) {
    infoRows.push(`
      <div class="order-info-row">
        <span>Orden:</span>
        <span><strong>#${order.order_number}</strong></span>
      </div>
    `);
    infoRows.push(`
      <div class="order-info-row">
        <span>Fecha:</span>
        <span>${formatTime(order.created_at, true)}</span>
      </div>
    `);
  } else {
    infoRows.push(`
      <div class="receipt-info-row">
        <span><strong>Folio:</strong> #${order.ticket_number || order.order_number}</span>
      </div>
    `);
    const dateTime = formatTime(order.created_at, true).split(',');
    infoRows.push(`
      <div class="receipt-info-row">
        <span><strong>Fecha:</strong> ${dateTime[0]}</span>
      </div>
    `);
    infoRows.push(`
      <div class="receipt-info-row">
        <span><strong>Hora:</strong> ${dateTime[1]?.trim() || ''}</span>
      </div>
    `);
  }

  if (order.table_number) {
    infoRows.push(`
      <div class="${isPreBill ? 'order-info-row' : 'receipt-info-row'}">
        <span>${isPreBill ? 'Mesa:' : '<strong>Mesa:</strong>'}</span>
        <span>${isPreBill ? `<strong>${order.table_number}</strong>` : order.table_number}</span>
      </div>
    `);
  }

  if (order.customer_name && (!order.table_number || isPreBill)) {
    infoRows.push(`
      <div class="${isPreBill ? 'order-info-row' : 'receipt-info-row'}">
        <span>${isPreBill ? 'Cliente:' : '<strong>Cliente:</strong>'}</span>
        <span>${order.customer_name}</span>
      </div>
    `);
  }

  const waiterName = order.waiter?.name || order.waiter_name;
  if (waiterName) {
    infoRows.push(`
      <div class="${isPreBill ? 'order-info-row' : 'receipt-info-row'}">
        <span>${isPreBill ? 'Atendió:' : '<strong>Atendió:</strong>'}</span>
        <span>${waiterName}</span>
      </div>
    `);
  }

  if (isPreBill) {
    infoRows.push(`
      <div class="order-info-row">
        <span>Tipo:</span>
        <span>${getOrderTypeLabel(order.order_type)}</span>
      </div>
    `);
  }

  return infoRows.join('');
};

export const createTotalsSection = (order: CustomerPrintOrder, isPreBill = false) => {
  const { subtotal, discount, tax, total, ivaAmount } = calculateOrderTotals(order);
  const totalsClass = isPreBill ? 'totals-section' : 'receipt-totals';
  const finalRowClass = isPreBill ? 'total-row final' : 'total-row grand-total';

  return `
  <div class="${totalsClass}">
    <div class="total-row">
      <span>Subtotal:</span>
      <span>${formatCurrency(subtotal)}</span>
    </div>
    ${discount > 0 ? `
    <div class="total-row">
      <span>Descuento:</span>
      <span>-${formatCurrency(discount)}</span>
    </div>
    ` : ''}
    ${tax > 0 ? `
    <div class="total-row">
      <span>IVA:</span>
      <span>${formatCurrency(tax)}</span>
    </div>
    ` : ''}
    ${!isPreBill && order.iva_enabled && ivaAmount > 0 ? `
    <div class="total-row" style="color: #0066cc;">
      <span>IVA (${((order.iva_rate || 0) * 100).toFixed(0)}%):</span>
      <span>${formatCurrency(ivaAmount)}</span>
    </div>
    ` : ''}
    <div class="${finalRowClass}">
      <span>TOTAL:</span>
      <span>${formatCurrency(total)}</span>
    </div>
  </div>
  `;
};

export const createPaymentSection = (order: CustomerPrintOrder) => {
  if (!order.payment_method) return '';

  const { total } = calculateOrderTotals(order);

  return `
  <div class="receipt-payment">
    <div class="payment-row">
      <span><strong>Método de Pago:</strong></span>
      <span>${getPaymentMethodLabel(order.payment_method)}</span>
    </div>
    ${order.payment_method === 'cash' && order.amount_paid ? `
    <div class="payment-row">
      <span>Pagó con:</span>
      <span>${formatCurrency(order.amount_paid)}</span>
    </div>
    <div class="payment-row change">
      <span>Cambio:</span>
      <span>${formatCurrency(order.amount_paid - total)}</span>
    </div>
    ` : ''}
  </div>
  `;
};

export const createFooterSection = (isPreBill = false) => {
  if (isPreBill) {
    return `
  <div class="footer">
    Este no es un comprobante de pago<br>
    Solicite su ticket fiscal al realizar el pago
  </div>
  `;
  }

  return `
  <div class="receipt-footer">
    <div class="footer-message">¡Gracias por su preferencia!</div>
    <div class="footer-message">¡Vuelva pronto!</div>
    <div style="margin-top: 8px; font-size: 9px;">
      Este ticket no es un comprobante fiscal
    </div>
  </div>
  `;
};

const createPreBillItemHTML = (item: any) => {
  const { itemName, variantName, categoryName, subtotal } = extractItemData(item);

  return `
    <div class="item-row">
      <div class="item-main">
        <span class="item-qty">${item.quantity}x</span>
        <span class="item-name">${itemName}${variantName ? ` (${variantName})` : ''}</span>
      </div>
      <span class="item-price">${formatCurrency(subtotal)}</span>
    </div>
    ${categoryName ? `<div class="item-category">${categoryName}</div>` : ''}
  `;
};

const createReceiptItemHTML = (item: any) => {
  const { itemName, variantName, categoryName, quantity, subtotal } = extractItemData(item);

  return `
    <tr class="receipt-item">
      <td class="item-quantity">${quantity}</td>
      <td class="item-description">
        <div class="item-name">${itemName}${variantName ? ` (${variantName})` : ''}</div>
        ${categoryName ? `<div class="item-category">${categoryName}</div>` : ''}
      </td>
      <td class="item-total">${formatCurrency(subtotal)}</td>
    </tr>
  `;
};

const createReceiptPersonSection = (personName: string, itemsHTML: string) => `
  <tr class="person-section">
    <td colspan="3" class="person-header">${personName}</td>
  </tr>
  ${itemsHTML}
`;

export const buildItemsHTML = (order: CustomerPrintOrder, isPreBill = false) => {
  if (isPreBill) {
    const { html } = buildGroupedItemsHTML(order, createPreBillItemHTML);
    return html;
  }

  if (!order.persons || order.persons.length <= 1) {
    return (order.items || []).map(createReceiptItemHTML).join('');
  }

  let itemsHTML = order.persons
    .map((person: any, index: number) => {
      const personItems = order.items?.filter((item: any) => item.person_id === person.id) || [];
      const items = personItems.length > 0 ? personItems : (person.items || []);
      if (items.length === 0) return '';

      const personName = person.name || `Persona ${index + 1}`;
      return createReceiptPersonSection(personName, items.map(createReceiptItemHTML).join(''));
    })
    .join('');

  if (!itemsHTML && order.items?.length) {
    itemsHTML = order.items.map(createReceiptItemHTML).join('');
  }

  return itemsHTML;
};

export const createItemsBlock = (itemsHTML: string, isPreBill: boolean) =>
  isPreBill
    ? `<div class="items-section">${itemsHTML}</div>`
    : `
  <div class="receipt-items">
    <table class="items-table">
      <thead>
        <tr>
          <th>Cant.</th>
          <th>Desc.</th>
          <th class="text-right">Total</th>
        </tr>
      </thead>
      <tbody>
        ${itemsHTML}
      </tbody>
    </table>
  </div>`;

export const createMiddleBlock = ({
  isPreBill,
  orderInfoHTML,
  itemsBlock,
  totalsHTML,
  paymentHTML,
  footerHTML
}: {
  isPreBill: boolean;
  orderInfoHTML: string;
  itemsBlock: string;
  totalsHTML: string;
  paymentHTML: string;
  footerHTML: string;
}) =>
  isPreBill
    ? `
  <div class="bill-title">PRE-CUENTA</div>

  <div class="order-info">${orderInfoHTML}</div>

  <div class="divider"></div>

  ${itemsBlock}

  ${totalsHTML}

  ${footerHTML}`
    : `
  <div class="receipt-divider"></div>

  <div class="receipt-info">${orderInfoHTML}</div>

  <div class="receipt-divider"></div>

  ${itemsBlock}

  ${totalsHTML}

  ${paymentHTML}

  <div class="receipt-divider"></div>

  ${footerHTML}`;
